// reference: https://github.com/facebook/lexical/blob/main/packages/lexical-playground/src/plugins/ToolbarPlugin/utils.ts
import { $isHeadingNode } from '@lexical/rich-text';
import { $getNearestBlockElementAncestorOrThrow, $isBlockFullySelected } from '@lexical/utils';
import {
    $createParagraphNode,
    $getSelection,
    $isRangeSelection,
    $isTextNode,
    ElementNode,
    LexicalEditor,
    NodeKey,
} from 'lexical';

const $clearBlockFormat = (block: ElementNode) => {
    if (block.getFormat() !== 0) {
        block.setFormat('');
    }
    if (block.getIndent() !== 0) {
        block.setIndent(0);
    }
};

export const clearFormatting = (editor: LexicalEditor) => {
    editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
            const anchor = selection.anchor;
            const focus = selection.focus;
            const extractedNodes = selection.extract();

            if (anchor.key === focus.key && anchor.offset === focus.offset) {
                $clearBlockFormat($getNearestBlockElementAncestorOrThrow(anchor.getNode()));
                return;
            }
            // Determine which blocks are fully selected before making any
            // changes, since the mutations below (such as replacing a
            // HeadingNode with a ParagraphNode) would detach nodes that the
            // selection's carets may refer to
            const postExtractSelection = $getSelection();
            let fullySelectedBlocks: null | Set<NodeKey> = null;
            if ($isRangeSelection(postExtractSelection)) {
                fullySelectedBlocks = new Set();
                for (const node of extractedNodes) {
                    if ($isTextNode(node)) {
                        const block = $getNearestBlockElementAncestorOrThrow(node);
                        if (
                            !fullySelectedBlocks.has(block.getKey()) &&
                            $isBlockFullySelected(block, postExtractSelection)
                        ) {
                            fullySelectedBlocks.add(block.getKey());
                        }
                    }
                }
            }

            extractedNodes.forEach((node) => {
                if ($isTextNode(node)) {
                    if (node.getStyle() !== '') {
                        node.setStyle('');
                    }
                    if (node.getFormat() !== 0) {
                        node.setFormat(0);
                    }
                    const nearestBlockElement = $getNearestBlockElementAncestorOrThrow(node);
                    if (fullySelectedBlocks === null || fullySelectedBlocks.has(nearestBlockElement.getKey())) {
                        $clearBlockFormat(nearestBlockElement);
                    }
                } else if ($isHeadingNode(node)) {
                    node.replace($createParagraphNode(), true);
                }
            });

            // https://github.com/facebook/lexical/pull/8889
            const clearedSelection = $getSelection();
            if ($isRangeSelection(clearedSelection)) {
                clearedSelection.setFormat(0);
                clearedSelection.setStyle('');
            }
        }
    });
};
